/**
 * Whether a `User-Agent` belongs to a crawler.
 *
 * The question this answers is "can this client meaningfully hold a preference?", not "is this report
 * worth a ticket". That distinction matters and is the reason this module exists in this shape: the
 * first honest use of it is deciding whether to load a consent CMP, and a crawler is the one client
 * for which a cookie banner is meaningless — it consents to nothing, stores nothing, and sees nothing.
 *
 * Deliberately NOT a gate on error reporting. TEC-3747 looked like crawler noise and was not: chasing
 * why crawlers failed found a real delivery bug of ours (`upload-to-cdn.ts` served one brotli
 * representation to every client regardless of `Accept-Encoding`). Suppressing those reports would
 * have hidden it. A crawler failing on our page is evidence, and this predicate must never be used to
 * throw that evidence away.
 *
 * Deliberately NOT the bot regex in `html-limited-bots.ts` either. That one decides what a crawler is
 * SERVED — whether SEO metadata blocks the render — so widening it changes what we render. The two
 * lists answer different questions and must stay independent.
 */

/**
 * Crawlers that identify themselves by name. Matched as plain substrings against the lowercased
 * user agent, because a crawler token can appear anywhere in it: Meta's sits inside a `(compatible;
 * ...)` comment behind a full Chrome product string, so anchoring would miss it.
 *
 * NOT in this list, on purpose: `whatsapp`, `instagram`, `fbav`, `line`. Those tokens identify in-app
 * *browsers* as much as link-preview fetchers, and portals take real donation traffic through them.
 * A person donating from inside the WhatsApp browser is owed a consent banner like anyone else.
 */
const DECLARED_CRAWLERS = [
	// Meta. `meta-externalagent` is the one in TEC-3747; the other two are its older link scrapers.
	'meta-externalagent',
	'facebookexternalhit',
	'facebookcatalog',
	// Search engines.
	'googlebot',
	'google-inspectiontool',
	'storebot-google',
	'chrome-lighthouse',
	'bingbot',
	'bingpreview',
	'applebot',
	'duckduckbot',
	'yandexbot',
	'baiduspider',
	'seznambot',
	'petalbot',
	'slurp',
	'sogou',
	// AI and dataset crawlers.
	'gptbot',
	'oai-searchbot',
	'chatgpt-user',
	'claudebot',
	'claude-web',
	'anthropic-ai',
	'perplexitybot',
	'amazonbot',
	'bytespider',
	'ccbot',
	// SEO and archival.
	'ahrefsbot',
	'semrushbot',
	'mj12bot',
	'dotbot',
	'ia_archiver',
	// Link unfurlers.
	'twitterbot',
	'linkedinbot',
	'slackbot',
	'discordbot',
	'telegrambot',
	'redditbot',
	'pinterestbot',
	'embedly',
	'quora link preview',
	'vkshare',
	'tumblr',
	'bitlybot',
];

/**
 * Undeclared-but-conventional crawlers, caught by the shape of the user agent rather than by name.
 *
 * Three markers, each chosen because a real browser never emits it:
 *
 * - `…bot/` — the `Product/Version` form a crawler uses for its own name (`Googlebot/2.1`).
 * - `crawler` / `spider` — self-describing words no browser ships.
 * - `+http` — the "who to contact" URL convention, as in `(+https://example.com/bot.html)`.
 *
 * A bare `/bot/i` would have been the obvious rule and is the trap here: real phones ship user agents
 * like `Linux; Android 11; CUBOT NOTE 20 Build/RP1A.200720.011`, and matching those would drop every
 * error report from a whole device class without anyone noticing. Requiring the slash is what keeps
 * `CUBOT NOTE` out while keeping `SomeNewBot/1.0` in.
 */
const CONVENTIONAL_CRAWLER = /[a-z0-9._-]bot\/|crawler|spider|\+http/;

/**
 * Undefined is NOT treated as a crawler. A missing user agent is an absence of evidence, and the
 * failure modes are asymmetric: withholding the banner from a person is a consent defect, while
 * loading a CMP for a crawler costs only that crawler's bandwidth. When unsure, treat it as a person.
 */
export function isCrawlerUserAgent(userAgent: string | undefined): boolean {
	if (!userAgent) return false;
	const ua = userAgent.toLowerCase();
	return DECLARED_CRAWLERS.some((crawler) => ua.includes(crawler)) || CONVENTIONAL_CRAWLER.test(ua);
}
